import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

export const COLOR_IN = '#2a78d6';
export const COLOR_OUT = '#eb6834';
export const COLOR_FAIL = '#d32f2f';
const HEIGHT = 170;
const TOP = 8;
const BOTTOM = 22;
const LEFT = 34;
const HOUR = 3600e3;
const pad = (n) => String(n).padStart(2, '0');

export const hourLabel = (ms) => {
    const t = new Date(ms);
    return `${pad(t.getMonth() + 1)}.${pad(t.getDate())}. ${pad(t.getHours())}:00–${pad(
        (t.getHours() + 1) % 24
    )}:00`;
};

// start of the first hourly bucket for the chosen range (the last bucket is the current hour)
export function timelineStart(hours) {
    const end = new Date();
    end.setMinutes(60, 0, 0);
    return end.getTime() - hours * HOUR;
}

// bar with rounded top corners only (anchored to the baseline)
function barPath(x, base, w, h, r) {
    if (h <= 0) return '';
    const rr = Math.min(r, h);
    return `M${x},${base} V${base - h + rr} Q${x},${base - h} ${x + rr},${base - h} H${
        x + w - rr
    } Q${x + w},${base - h} ${x + w},${base - h + rr} V${base} Z`;
}

/**
 * Hourly stacked bars (entries at the baseline, exits on top), click a bar to filter by that hour.
 */
export default function Timeline({ list, hours, start: startAt, selectedHour, onSelectHour }) {
    const theme = useTheme();
    const ref = useRef(null);
    const [width, setWidth] = useState(800);
    const [tip, setTip] = useState(null);

    useEffect(() => {
        const ro = new ResizeObserver(([entry]) =>
            setWidth(Math.max(300, Math.floor(entry.contentRect.width)))
        );
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, []);

    const start = startAt || timelineStart(hours);
    // entries / exits as bars, failures (rejections, errors) as a red line on top
    const bins = Array.from({ length: hours }, () => ({ in: 0, out: 0, fail: 0 }));
    list.forEach((e) => {
        const i = Math.floor((e.ts - start) / HOUR);
        if (i < 0 || i >= hours) return;
        if (e.dir) bins[i][e.dir] += 1;
        if (e.sev === 'fail') bins[i].fail += 1;
    });

    const max = Math.max(1, ...bins.map((b) => Math.max(b.in + b.out, b.fail)));
    const step = 10 ** Math.floor(Math.log10(max));
    const tick = [1, 2, 5, 10].map((m) => m * step).find((t) => max / t <= 4);
    const yMax = Math.ceil(max / tick) * tick;
    const ph = HEIGHT - TOP - BOTTOM;
    const bw = (width - LEFT) / hours;
    const gap = Math.max(1, Math.min(6, bw * 0.25));
    const y = (v) => TOP + ph - (v / yMax) * ph;
    // keep x labels ~56px apart
    const labelEvery = [1, 2, 3, 6, 12, 24].find((n) => n * bw >= 56) || 24;
    const grid = [];
    for (let v = 0; v <= yMax; v += tick) grid.push(v);

    return (
        <Box
            ref={ref}
            sx={{ position: 'relative', height: HEIGHT }}
            onMouseLeave={() => setTip(null)}
        >
            <svg width={width} height={HEIGHT} style={{ display: 'block' }}>
                {grid.map((v) => (
                    <g key={v}>
                        <line
                            x1={LEFT}
                            x2={width}
                            y1={y(v)}
                            y2={y(v)}
                            stroke={theme.palette.divider}
                        />
                        <text
                            x={LEFT - 6}
                            y={y(v) + 4}
                            textAnchor="end"
                            fontSize="11"
                            fill={theme.palette.text.secondary}
                        >
                            {v}
                        </text>
                    </g>
                ))}
                {bins.map((b, i) => {
                    const t = start + i * HOUR;
                    const x = LEFT + i * bw + gap / 2;
                    const w = Math.max(1, bw - gap);
                    const hIn = (b.in / yMax) * ph;
                    const hOut = (b.out / yMax) * ph;
                    const r = Math.min(4, w / 2);
                    const h = new Date(t).getHours();
                    return (
                        <g key={t}>
                            {selectedHour === t && (
                                <rect
                                    x={LEFT + i * bw}
                                    y={TOP}
                                    width={bw}
                                    height={ph}
                                    fill={theme.palette.action.selected}
                                />
                            )}
                            {b.in > 0 && (
                                <path fill={COLOR_IN} d={barPath(x, y(0), w, hIn, b.out ? 0 : r)} />
                            )}
                            {b.out > 0 && (
                                <path
                                    fill={COLOR_OUT}
                                    d={barPath(
                                        x,
                                        y(0) - hIn - (b.in ? 2 : 0),
                                        w,
                                        Math.max(0, hOut - (b.in ? 2 : 0)),
                                        r
                                    )}
                                />
                            )}
                            {h % labelEvery === 0 && (
                                <text
                                    x={LEFT + i * bw}
                                    y={HEIGHT - 6}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fill={theme.palette.text.secondary}
                                >
                                    {h === 0
                                        ? `${pad(new Date(t).getMonth() + 1)}.${pad(
                                              new Date(t).getDate()
                                          )}.`
                                        : `${pad(h)}:00`}
                                </text>
                            )}
                            <rect
                                x={LEFT + i * bw}
                                y={TOP}
                                width={bw}
                                height={ph}
                                fill="transparent"
                                style={{ cursor: 'pointer' }}
                                onMouseMove={(ev) => {
                                    const box = ref.current.getBoundingClientRect();
                                    setTip({
                                        x: ev.clientX - box.left,
                                        y: ev.clientY - box.top,
                                        t,
                                        b,
                                    });
                                }}
                                onClick={() => onSelectHour(selectedHour === t ? null : t)}
                            />
                        </g>
                    );
                })}
                <polyline
                    fill="none"
                    stroke={COLOR_FAIL}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    points={bins.map((b, i) => `${LEFT + (i + 0.5) * bw},${y(b.fail)}`).join(' ')}
                    pointerEvents="none"
                />
                {bins.map((b, i) =>
                    b.fail ? (
                        <circle
                            key={`f${start + i * HOUR}`}
                            cx={LEFT + (i + 0.5) * bw}
                            cy={y(b.fail)}
                            r="3"
                            fill={COLOR_FAIL}
                            pointerEvents="none"
                        />
                    ) : null
                )}
            </svg>
            {tip && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: Math.min(Math.max(0, tip.x - 90), width - 180),
                        top: Math.max(0, tip.y - 64),
                        width: 180,
                        pointerEvents: 'none',
                        bgcolor: 'text.primary',
                        color: 'background.paper',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                    }}
                >
                    <Typography variant="caption" component="div">
                        {hourLabel(tip.t)}
                    </Typography>
                    <Typography variant="caption" component="div">
                        Behajtás: <b>{tip.b.in}</b> · Kihajtás: <b>{tip.b.out}</b> · Hiba:{' '}
                        <b>{tip.b.fail}</b>
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
